/**
 * @file 钱包登录管理器（MetaMask / WalletConnect 等）
 * Google 登录已改用 Firebase，见 @/utils/firebase.js
 */

import { loadEthers } from '@/utils/load-ethers';
import { createWalletLoginSignPayload } from '@/utils/wallet-login-message';
import { isUserRejectedError } from '@/utils/trade-error-map';

/**
 * EIP-6963：多注入钱包发现。每个钱包通过 announceProvider 事件上报自己的
 * provider 和唯一标识 rdns（如 io.metamask / app.phantom），从根本上避免
 * 多钱包争抢 window.ethereum 造成的「冒充」问题。
 * 模块加载即开始监听并请求一次，点击钱包时（远晚于加载）map 已填充。
 */
const eip6963Providers = new Map(); // rdns -> { info, provider }
if (typeof window !== 'undefined') {
  window.addEventListener('eip6963:announceProvider', (event) => {
    const detail = event.detail;
    if (detail?.info?.rdns && detail.provider) {
      eip6963Providers.set(detail.info.rdns, detail);
    }
  });
  window.dispatchEvent(new Event('eip6963:requestProvider'));
}

/** 按 rdns 列表从 EIP-6963 上报里取 provider */
function getProviderByRdns(rdnsList) {
  for (const rdns of rdnsList) {
    const detail = eip6963Providers.get(rdns);
    if (detail?.provider) return detail.provider;
  }
  return null;
}

/**
 * 多钱包共存时，window.ethereum 可能是某个具体钱包，也可能带 providers[] 数组。
 * 按特征标识从中挑出目标 provider，避免「装了 MetaMask 点 Coinbase 却开了 MetaMask」。
 * @param {(p: any) => boolean} match 特征判断
 * @returns {any|null}
 */
function pickInjectedProvider(match) {
  if (typeof window === 'undefined') return null;
  const eth = window.ethereum;
  if (!eth) return null;
  if (Array.isArray(eth.providers) && eth.providers.length) {
    return eth.providers.find((p) => p && match(p)) || null;
  }
  return match(eth) ? eth : null;
}

/** 真·MetaMask：优先 EIP-6963 精确取，回退到特征探测（排除冒充者） */
export function getMetaMaskProvider() {
  const byRdns = getProviderByRdns(['io.metamask', 'io.metamask.flask']);
  if (byRdns) return byRdns;
  return pickInjectedProvider(
    (p) => p.isMetaMask && !p.isCoinbaseWallet && !p.isOkxWallet && !p.isBitKeep && !p.isPhantom
  );
}

/** Coinbase：优先 EIP-6963，其次专用注入对象，最后从 providers[] 找 isCoinbaseWallet */
export function getCoinbaseProvider() {
  if (typeof window === 'undefined') return null;
  const byRdns = getProviderByRdns(['com.coinbase.wallet']);
  if (byRdns) return byRdns;
  if (window.coinbaseWalletExtension) return window.coinbaseWalletExtension;
  return pickInjectedProvider((p) => p.isCoinbaseWallet);
}

/** OKX 有独立注入对象 */
export function getOkxProvider() {
  return (typeof window !== 'undefined' && window.okxwallet) || null;
}

/** Bitget(BitKeep) 有独立注入对象 */
export function getBitgetProvider() {
  return (typeof window !== 'undefined' && window.bitkeep?.ethereum) || null;
}

/** Phantom(Solana) */
export function getPhantomProvider() {
  return (typeof window !== 'undefined' && window.phantom?.solana) || null;
}

/** TronLink */
export function hasTronLink() {
  return typeof window !== 'undefined' && Boolean(window.tronLink && window.tronWeb);
}

/**
 * Base58 编码（Solana / 比特币字母表）。Solana 签名惯例用 base58 表示。
 * 内置实现，避免为单一用途引入 bs58 依赖。
 * @param {Uint8Array|ArrayLike<number>} input
 * @returns {string}
 */
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
function base58Encode(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let zeros = 0;
  while (zeros < bytes.length && bytes[zeros] === 0) zeros += 1;
  const digits = [];
  for (let i = zeros; i < bytes.length; i += 1) {
    let carry = bytes[i];
    for (let j = 0; j < digits.length; j += 1) {
      carry += digits[j] << 8;
      digits[j] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }
  let result = '';
  for (let i = 0; i < zeros; i += 1) result += BASE58_ALPHABET[0];
  for (let i = digits.length - 1; i >= 0; i -= 1) result += BASE58_ALPHABET[digits[i]];
  return result;
}

async function normalizeEvmAddress(address) {
  const value = String(address ?? '').trim();
  if (!/^0x[0-9a-fA-F]{40}$/.test(value)) return value;
  try {
    const { getAddress } = await loadEthers();
    return getAddress(value);
  } catch {
    return value;
  }
}

/**
 * 生成后端验签用的 message（格式：nonce + 换行 + walletAddress）
 */
async function getChallengeMessageFromServer(address) {
  const normalized = await normalizeEvmAddress(address);
  const { message } = createWalletLoginSignPayload(normalized);
  return message;
}

export const WALLET_LOGIN_ERROR = {
  NOT_INSTALLED: 'NOT_INSTALLED',
  REJECTED: 'REJECTED',
  LOCKED: 'LOCKED',
  NO_ACCOUNT: 'NO_ACCOUNT',
  PENDING_REQUEST: 'PENDING_REQUEST',
  CONNECT_FAILED: 'CONNECT_FAILED',
  TIMEOUT: 'TIMEOUT',
};

export const WALLET_LOGIN_PHASE = {
  CONNECT: 'connect',
  SIGN: 'sign',
};

const WALLET_SILENT_ACCOUNTS_TIMEOUT_MS = 5_000;
const WALLET_CONNECT_TIMEOUT_MS = 45_000;
const WALLET_SIGN_TIMEOUT_MS = 60_000;

function throwIfAborted(signal, walletId) {
  if (signal?.aborted) {
    walletLoginFail(WALLET_LOGIN_ERROR.REJECTED, walletId);
  }
}

/**
 * @param {Promise<any>} promise
 * @param {number} ms
 * @param {{ signal?: AbortSignal, walletId?: string, phase?: string, throwOnTimeout?: boolean }} [options]
 */
async function raceWithTimeout(promise, ms, options = {}) {
  const { signal, walletId = '', phase, throwOnTimeout = true } = options;
  throwIfAborted(signal, walletId);

  let timer;
  let onAbort;

  const timeoutPromise = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      if (!throwOnTimeout) {
        resolve(null);
        return;
      }
      const err = new WalletLoginError(WALLET_LOGIN_ERROR.TIMEOUT, walletId);
      err.phase = phase;
      reject(err);
    }, ms);
  });

  const abortPromise = signal
    ? new Promise((_, reject) => {
        onAbort = () => {
          reject(new WalletLoginError(WALLET_LOGIN_ERROR.REJECTED, walletId));
        };
        signal.addEventListener('abort', onAbort, { once: true });
      })
    : null;

  try {
    const racers = [promise, timeoutPromise];
    if (abortPromise) racers.push(abortPromise);
    return await Promise.race(racers);
  } finally {
    if (timer) clearTimeout(timer);
    if (signal && onAbort) signal.removeEventListener('abort', onAbort);
  }
}

async function requestInjectedAccounts(
  provider,
  requestMethod,
  walletId,
  { signal, onPhase } = {}
) {
  onPhase?.(WALLET_LOGIN_PHASE.CONNECT);

  const silentAccounts = await raceWithTimeout(
    provider.request({ method: 'eth_accounts' }),
    WALLET_SILENT_ACCOUNTS_TIMEOUT_MS,
    { signal, walletId, phase: WALLET_LOGIN_PHASE.CONNECT, throwOnTimeout: false }
  );
  if (Array.isArray(silentAccounts) && silentAccounts[0]) {
    return silentAccounts;
  }

  return raceWithTimeout(provider.request({ method: requestMethod }), WALLET_CONNECT_TIMEOUT_MS, {
    signal,
    walletId,
    phase: WALLET_LOGIN_PHASE.CONNECT,
  });
}

export class WalletLoginError extends Error {
  constructor(code, walletId = '') {
    super(code);
    this.name = 'WalletLoginError';
    this.code = code;
    this.walletId = walletId;
  }
}

function walletLoginFail(code, walletId) {
  throw new WalletLoginError(code, walletId);
}

class LoginManager {
  constructor() {
    this.providers = {
      metamask: (options) =>
        this.loginWithInjectedWallet(
          getMetaMaskProvider(),
          'eth_requestAccounts',
          'personal_sign',
          'metamask',
          options
        ),
      okx: (options) =>
        this.loginWithInjectedWallet(
          getOkxProvider(),
          'eth_requestAccounts',
          'personal_sign',
          'okx',
          options
        ),
      bitget: (options) =>
        this.loginWithInjectedWallet(
          getBitgetProvider(),
          'eth_requestAccounts',
          'personal_sign',
          'bitget',
          options
        ),
      coinbase: (options) =>
        this.loginWithInjectedWallet(
          getCoinbaseProvider(),
          'eth_requestAccounts',
          'personal_sign',
          'coinbase',
          options
        ),
      tronlink: (options) => this.loginWithTronLink(options),
      phantom: (options) => this.loginWithPhantom(options),
    };
  }

  /**
   * @param {string} providerName
   * @param {{ signal?: AbortSignal, onPhase?: (phase: string) => void }} [options]
   */
  async loginWith(providerName, options = {}) {
    const key = providerName.toLowerCase();
    const provider = this.providers[key];
    if (!provider) {
      walletLoginFail(WALLET_LOGIN_ERROR.CONNECT_FAILED, key);
    }
    return provider(options);
  }

  /**
   * 仅连接注入式 EVM 钱包并返回地址（不做 DeepSage challenge 签名）。
   * 登录主路径请用 `loginWith`；本方法供充值 Connected 等只需拿 provider 的场景。
   * @param {string} walletId
   * @param {{ signal?: AbortSignal, onPhase?: (phase: string) => void }} [options]
   * @returns {Promise<{ address: string, provider: any }>}
   */
  async connectInjectedWallet(walletId, { signal, onPhase } = {}) {
    const getters = {
      metamask: getMetaMaskProvider,
      okx: getOkxProvider,
      coinbase: getCoinbaseProvider,
      bitget: getBitgetProvider,
    };
    const key = String(walletId ?? '')
      .trim()
      .toLowerCase();
    const injectedProvider = getters[key]?.();
    if (!injectedProvider) {
      walletLoginFail(WALLET_LOGIN_ERROR.NOT_INSTALLED, key);
    }

    try {
      throwIfAborted(signal, key);
      const accounts = await requestInjectedAccounts(injectedProvider, 'eth_requestAccounts', key, {
        signal,
        onPhase,
      });
      const address = await normalizeEvmAddress(accounts[0]);
      if (!address) {
        walletLoginFail(WALLET_LOGIN_ERROR.NO_ACCOUNT, key);
      }
      return { address, provider: injectedProvider };
    } catch (error) {
      console.error('钱包连接失败:', error);
      if (error instanceof WalletLoginError) throw error;
      if (isUserRejectedError(error)) {
        walletLoginFail(WALLET_LOGIN_ERROR.REJECTED, key);
      }
      walletLoginFail(WALLET_LOGIN_ERROR.LOCKED, key);
    }
  }

  /**
   * 处理与 EVM 兼容的注入式钱包（如 MetaMask, OKX）的登录和签名流程
   */
  async loginWithInjectedWallet(
    injectedProvider,
    requestMethod,
    signMethod,
    walletId,
    { signal, onPhase } = {}
  ) {
    if (!injectedProvider) {
      walletLoginFail(WALLET_LOGIN_ERROR.NOT_INSTALLED, walletId);
    }

    try {
      throwIfAborted(signal, walletId);
      const accounts = await requestInjectedAccounts(injectedProvider, requestMethod, walletId, {
        signal,
        onPhase,
      });
      const address = await normalizeEvmAddress(accounts[0]);
      if (!address) {
        walletLoginFail(WALLET_LOGIN_ERROR.NO_ACCOUNT, walletId);
      }

      const message = await getChallengeMessageFromServer(address);

      onPhase?.(WALLET_LOGIN_PHASE.SIGN);

      let signature;
      if (signMethod === 'personal_sign') {
        signature = await raceWithTimeout(
          injectedProvider.request({
            method: 'personal_sign',
            params: [message, address],
          }),
          WALLET_SIGN_TIMEOUT_MS,
          { signal, walletId, phase: WALLET_LOGIN_PHASE.SIGN }
        );
      } else if (signMethod === 'signMessage') {
        const encodedMessage = new TextEncoder().encode(message);
        const signedMessage = await raceWithTimeout(
          injectedProvider.signMessage(encodedMessage, 'utf8'),
          WALLET_SIGN_TIMEOUT_MS,
          { signal, walletId, phase: WALLET_LOGIN_PHASE.SIGN }
        );
        signature = Buffer.from(signedMessage.signature).toString('hex');
      } else {
        walletLoginFail(WALLET_LOGIN_ERROR.CONNECT_FAILED, walletId);
      }

      return { address, signature, message };
    } catch (error) {
      console.error('钱包登录或签名失败:', error);
      if (error instanceof WalletLoginError) throw error;
      if (isUserRejectedError(error)) {
        walletLoginFail(WALLET_LOGIN_ERROR.REJECTED, walletId);
      }
      walletLoginFail(WALLET_LOGIN_ERROR.LOCKED, walletId);
    }
  }

  /**
   * 处理 TronLink 登录。它有自己独特的 API。
   */
  async loginWithTronLink(_options = {}) {
    const walletId = 'tronlink';
    const { tronLink } = window;
    if (!tronLink && !window.tronWeb) {
      walletLoginFail(WALLET_LOGIN_ERROR.NOT_INSTALLED, walletId);
    }

    try {
      if (tronLink?.request) {
        const res = await tronLink.request({ method: 'tron_requestAccounts' });
        const code = res?.code;
        if (code === 4001) {
          walletLoginFail(WALLET_LOGIN_ERROR.REJECTED, walletId);
        }
        if (code === 4000) {
          walletLoginFail(WALLET_LOGIN_ERROR.PENDING_REQUEST, walletId);
        }
      }

      const tronWeb = window.tronWeb;
      const address = tronWeb?.defaultAddress?.base58;
      if (!address) {
        walletLoginFail(WALLET_LOGIN_ERROR.LOCKED, walletId);
      }

      const message = await getChallengeMessageFromServer(address);
      const hexMessage = tronWeb.toHex(message);
      const signature = await tronWeb.trx.sign(hexMessage);

      return { address, signature, message };
    } catch (error) {
      console.error('TronLink 登录或签名失败:', error);
      if (error instanceof WalletLoginError) throw error;
      const msg = error?.message || '';
      if (
        msg.includes('User denied') ||
        msg.includes('Confirmation declined') ||
        msg.includes('cancel')
      ) {
        walletLoginFail(WALLET_LOGIN_ERROR.REJECTED, walletId);
      }
      walletLoginFail(WALLET_LOGIN_ERROR.LOCKED, walletId);
    }
  }

  /**
   * 处理 Phantom(Solana) 登录
   */
  async loginWithPhantom(_options = {}) {
    const walletId = 'phantom';
    const provider = getPhantomProvider();
    if (!provider) {
      walletLoginFail(WALLET_LOGIN_ERROR.NOT_INSTALLED, walletId);
    }

    try {
      let publicKey = provider.publicKey;
      if (!publicKey) {
        const resp = await provider.connect();
        publicKey = resp?.publicKey ?? provider.publicKey;
      }
      const address = publicKey?.toString?.();
      if (!address) {
        walletLoginFail(WALLET_LOGIN_ERROR.NO_ACCOUNT, walletId);
      }

      const message = await getChallengeMessageFromServer(address);
      const encodedMessage = new TextEncoder().encode(message);
      const signed = await provider.signMessage(encodedMessage, 'utf8');
      const signatureBytes = signed?.signature ?? signed;
      const signature = base58Encode(signatureBytes);

      return { address, signature, message };
    } catch (error) {
      console.error('Phantom 登录或签名失败:', error);
      try {
        await provider.disconnect?.();
      } catch {
        /* ignore */
      }

      if (error instanceof WalletLoginError) throw error;
      const msg = error?.message || '';
      if (isUserRejectedError(error)) {
        walletLoginFail(WALLET_LOGIN_ERROR.REJECTED, walletId);
      }
      if (msg === 'NO_ACCOUNT') {
        walletLoginFail(WALLET_LOGIN_ERROR.NO_ACCOUNT, walletId);
      }
      walletLoginFail(WALLET_LOGIN_ERROR.LOCKED, walletId);
    }
  }

  /**
   * WalletConnect 连接并签名（委托 wallet-connect-session）
   * @param {(uri: string) => void} onUri
   * @param {AbortSignal} [signal]
   * @returns {Promise<{address, signature, message} | null>}
   */
  async connectWalletConnectForQR(onUri, signal) {
    const { connectAndSignWalletLogin } = await import('@/utils/wallet-connect-session');
    return connectAndSignWalletLogin({ onUri, signal });
  }
}

const loginManager = new LoginManager();
export default loginManager;
